/**
 * PauseScene
 *
 * Pause overlay with quick settings and game controls.
 * Similar structure to VictoryScene but for pausing the game.
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS, GAME_EVENTS } from '../config/constants';
import { ICONS } from '../config/icons';
import { SoundManager } from '../systems/SoundManager';
import { HapticManager } from '../systems/HapticManager';
import { SaveStateManager } from '../systems/SaveStateManager';
import { UsernameManager } from '../systems/UsernameManager';
import { LeaderboardManager } from '../systems/LeaderboardManager';
import { GlassButton } from '../ui/components/GlassButton';

export class PauseScene extends Phaser.Scene {
  private sfxToggleButton?: Phaser.GameObjects.Container;
  private musicToggleButton?: Phaser.GameObjects.Container;
  private hapticToggleButton?: Phaser.GameObjects.Container;
  private rankText?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'PauseScene' });
  }

  preload(): void {
    // Load SVG icons if not already loaded
    // Use high scale for retina/high-DPI displays (3x for mobile devices)
    const iconScale = Math.max(3, window.devicePixelRatio);

    if (!this.textures.exists('icon-play')) {
      this.load.svg('icon-play', '/play_arrow_48dp_E3E3E3_FILL1_wght400_GRAD0_opsz48.svg', {
        scale: iconScale,
      });
    }
    if (!this.textures.exists('icon-play-black')) {
      this.load.svg('icon-play-black', '/play_arrow_48dp_000000_FILL1_wght400_GRAD0_opsz48.svg', {
        scale: iconScale,
      });
    }
    if (!this.textures.exists('icon-sfx-on')) {
      this.load.svg('icon-sfx-on', '/icon-sfx-on.svg', { scale: iconScale });
      this.load.svg('icon-sfx-off', '/icon-sfx-off.svg', { scale: iconScale });
    }
    if (!this.textures.exists('icon-music-on')) {
      this.load.svg('icon-music-on', '/icon-music-on.svg', { scale: iconScale });
      this.load.svg('icon-music-off', '/icon-music-off.svg', { scale: iconScale });
    }
    if (!this.textures.exists('icon-vibration')) {
      this.load.svg(
        'icon-vibration',
        '/mobile_vibrate_48dp_E3E3E3_FILL1_wght400_GRAD0_opsz48.svg',
        { scale: iconScale }
      );
    }
    if (!this.textures.exists('icon-reset')) {
      this.load.svg('icon-reset', '/icon-reset.svg', { scale: iconScale });
    }
    if (!this.textures.exists('icon-share')) {
      this.load.svg('icon-share', '/share_48dp_E3E3E3_FILL1_wght400_GRAD0_opsz48.svg', {
        scale: iconScale,
      });
    }
    if (!this.textures.exists('icon-menu')) {
      this.load.svg('icon-menu', '/icon-menu.svg', { scale: iconScale });
    }
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Semi-transparent background overlay
    this.createBackground(width, height);

    // Pause title
    this.displayTitle(width, height);

    // Display current leaderboard rank (async, non-blocking)
    this.displayRank(width, height);

    // Large RESUME button (center)
    this.createResumeButton(width, height);

    // Quick settings toggles
    this.createQuickSettings(width, height);

    // Action buttons (Reset, Share, Main Menu)
    this.createActionButtons(width, height);
  }

  private createBackground(width: number, height: number): void {
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.8);
    bg.fillRect(0, 0, width, height);
    bg.setScrollFactor(0);
  }

  private displayTitle(width: number, height: number): void {
    const titleY = height * 0.15;
    const title = this.add.text(width / 2, titleY, 'PAUSED', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: '48px',
      color: '#FFD700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
      resolution: Math.max(3, window.devicePixelRatio),
    });
    title.setOrigin(0.5);
    title.setScrollFactor(0);

    // Pause icon to the left of title
    const iconSize = 40;
    const spacing = 15;
    const iconX = width / 2 - title.width / 2 - iconSize / 2 - spacing;
    const pauseIcon = this.add.image(iconX, titleY, ICONS.PAUSE);
    pauseIcon.setDisplaySize(iconSize, iconSize);
    pauseIcon.setTint(0xffd700); // Gold tint
    pauseIcon.setScrollFactor(0);
  }

  private async displayRank(width: number, height: number): Promise<void> {
    const username = UsernameManager.getUsername();
    const rankY = height * 0.24;

    // Create placeholder text (no icon yet)
    this.rankText = this.add.text(width / 2, rankY, 'Loading rank...', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: '18px',
      color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
      resolution: Math.max(3, window.devicePixelRatio),
    });
    this.rankText.setOrigin(0.5);
    this.rankText.setScrollFactor(0);

    try {
      // Fetch user's rank from combined leaderboard
      const rank = await LeaderboardManager.getUserRank(username);

      if (rank !== null) {
        this.rankText.setText(`Leaderboard Rank: #${rank}`);
        this.rankText.setColor('#4ECDC4');

        // Add trophy icon
        const iconSize = 18;
        const iconX = width / 2 - this.rankText.width / 2 - iconSize / 2 - 8;
        const trophyIcon = this.add.image(iconX, rankY, ICONS.TROPHY);
        trophyIcon.setDisplaySize(iconSize, iconSize);
        trophyIcon.setTint(0x4ecdc4); // Match text color
        trophyIcon.setScrollFactor(0);
      } else {
        this.rankText.setText(`Reach 20m+ for leaderboard entry`);
        this.rankText.setColor(GAME_CONSTANTS.UI.COLOR_SECONDARY);

        // Add trophy icon
        const iconSize = 18;
        const iconX = width / 2 - this.rankText.width / 2 - iconSize / 2 - 8;
        const trophyIcon = this.add.image(iconX, rankY, ICONS.TROPHY);
        trophyIcon.setDisplaySize(iconSize, iconSize);
        trophyIcon.setTint(0x9ca3af); // Match secondary color
        trophyIcon.setScrollFactor(0);
      }
    } catch (error) {
      console.error('Failed to fetch rank:', error);

      // Check if offline
      if (
        error instanceof Error &&
        (error.message === 'OFFLINE' || error.name === 'AbortError' || !navigator.onLine)
      ) {
        this.rankText.setText(`Offline - Can't check rank`);
        this.rankText.setColor('#FFA500');

        // Add wifi-off icon
        const iconSize = 18;
        const iconX = width / 2 - this.rankText.width / 2 - iconSize / 2 - 8;
        const wifiIcon = this.add.image(iconX, rankY, ICONS.WIFI_OFF);
        wifiIcon.setDisplaySize(iconSize, iconSize);
        wifiIcon.setTint(0xffa500); // Match text color
        wifiIcon.setScrollFactor(0);
      } else {
        this.rankText.setText(`Rank unavailable`);
        this.rankText.setColor(GAME_CONSTANTS.UI.COLOR_SECONDARY);

        // Add info icon
        const iconSize = 18;
        const iconX = width / 2 - this.rankText.width / 2 - iconSize / 2 - 8;
        const infoIcon = this.add.image(iconX, rankY, ICONS.INFO);
        infoIcon.setDisplaySize(iconSize, iconSize);
        infoIcon.setTint(0x9ca3af); // Match secondary color
        infoIcon.setScrollFactor(0);
      }
    }
  }

  private createResumeButton(width: number, height: number): void {
    const buttonY = height * 0.35;
    const buttonWidth = 280;
    const buttonHeight = 80;

    // Create container for the button
    const container = this.add.container(width / 2, buttonY);
    container.setScrollFactor(0);

    // Yellow background with rounded corners
    const bg = this.add.graphics();
    bg.fillStyle(0xffd700, 1.0); // Bright yellow
    bg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 16);
    // Add subtle shadow/border
    bg.lineStyle(3, 0x000000, 0.3);
    bg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 16);
    container.add(bg);

    // Black play icon (using colored SVG)
    const iconSize = 40;
    const icon = this.add.image(0, 0, ICONS.PLAY_CIRCLE_BLACK);
    icon.setDisplaySize(iconSize, iconSize);
    icon.setOrigin(0.5);
    container.add(icon);

    // Black "RESUME" text
    const text = this.add.text(0, 0, 'RESUME', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: '32px',
      color: '#000000',
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    text.setOrigin(0.5);
    container.add(text);

    // Position icon and text with proper spacing
    const spacing = 12;
    const contentWidth = iconSize + spacing + text.width;
    icon.setX(-contentWidth / 2 + iconSize / 2);
    text.setX(-contentWidth / 2 + iconSize + spacing + text.width / 2);

    // Create an invisible zone on top for interaction (critical for reliable touch input)
    const zone = this.add.zone(0, 0, buttonWidth, buttonHeight);
    zone.setInteractive({ useHandCursor: true });
    container.add(zone);

    // Store reference to active tweens to kill them if needed
    let hoverTween: Phaser.Tweens.Tween | null = null;

    // Hover effects
    zone.on('pointerover', () => {
      // Kill any existing tween
      if (hoverTween) {
        hoverTween.stop();
        hoverTween = null;
      }

      // Slight scale up on hover
      hoverTween = this.tweens.add({
        targets: container,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 100,
        ease: 'Power2',
        onComplete: () => {
          hoverTween = null;
        },
      });
    });

    zone.on('pointerout', () => {
      // Kill any existing tween
      if (hoverTween) {
        hoverTween.stop();
        hoverTween = null;
      }

      // Scale back to normal
      hoverTween = this.tweens.add({
        targets: container,
        scaleX: 1.0,
        scaleY: 1.0,
        duration: 100,
        ease: 'Power2',
        onComplete: () => {
          hoverTween = null;
        },
      });
    });

    zone.on('pointerdown', () => {
      // Kill any hover tweens first
      if (hoverTween) {
        hoverTween.stop();
        hoverTween = null;
      }

      HapticManager.buttonTap();

      // Resume immediately (don't wait for animation)
      this.resumeGame();

      // Quick press animation (visual feedback only, non-blocking)
      this.tweens.add({
        targets: container,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 50,
        yoyo: true,
        ease: 'Power2',
      });
    });
  }

  private createQuickSettings(width: number, height: number): void {
    const settingsY = height * 0.5;
    const titleY = settingsY - 50;

    // Section title
    const titleText = this.add.text(width / 2, titleY, 'Quick Settings', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: '20px',
      color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    titleText.setOrigin(0.5).setScrollFactor(0);

    // Settings icon to the left of title
    const iconSize = 20;
    const iconX = width / 2 - titleText.width / 2 - iconSize / 2 - 8;
    const settingsIcon = this.add.image(iconX, titleY, ICONS.SETTINGS);
    settingsIcon.setDisplaySize(iconSize, iconSize);
    settingsIcon.setTint(0x9ca3af); // Match secondary color
    settingsIcon.setScrollFactor(0);

    // Improved button layout - spread across width, double height
    const buttonSpacing = 20;
    const totalButtonWidth = width - 80; // Leave 40px margin on each side
    const buttonWidth = (totalButtonWidth - buttonSpacing * 2) / 3; // Divide into 3 equal buttons
    const buttonHeight = 64; // Double the height for better spacing
    const startX = 40; // Left margin

    // SFX Toggle
    const sfxIconKey = SoundManager.isSfxMuted() ? 'icon-sfx-off' : 'icon-sfx-on';
    this.sfxToggleButton = this.createIconButton(
      startX,
      settingsY,
      buttonWidth,
      buttonHeight,
      sfxIconKey,
      'SFX',
      () => this.toggleSfx()
    );

    // Music Toggle
    const musicIconKey = SoundManager.isMusicMuted() ? 'icon-music-off' : 'icon-music-on';
    this.musicToggleButton = this.createIconButton(
      startX + buttonWidth + buttonSpacing,
      settingsY,
      buttonWidth,
      buttonHeight,
      musicIconKey,
      'Music',
      () => this.toggleMusic()
    );

    // Haptic Toggle
    this.hapticToggleButton = this.createIconButton(
      startX + (buttonWidth + buttonSpacing) * 2,
      settingsY,
      buttonWidth,
      buttonHeight,
      'icon-vibration',
      'Haptic',
      () => this.toggleHaptic()
    );

    // Set initial haptic icon opacity based on state
    if (this.hapticToggleButton) {
      const icon = this.hapticToggleButton.getAt(1) as Phaser.GameObjects.Image;
      icon.setAlpha(HapticManager.isEnabled() ? 1.0 : 0.5);
    }
  }

  private createActionButtons(width: number, height: number): void {
    const buttonY = height * 0.7;

    // Reset button - using GlassButton for better touch targets
    const resetButton = new GlassButton(this, width / 2, buttonY, {
      text: 'Reset',
      iconKey: ICONS.REFRESH,
      fontSize: 20,
      width: 200,
      height: 52,
      style: 'secondary',
      onClick: () => this.resetGame(),
    });
    resetButton.setScrollFactor(0);

    // Share button
    const shareButton = new GlassButton(this, width / 2, buttonY + 70, {
      text: 'Share',
      iconKey: ICONS.SHARE,
      fontSize: 20,
      width: 200,
      height: 52,
      style: 'secondary',
      onClick: () => this.shareGame(),
    });
    shareButton.setScrollFactor(0);

    // Main Menu button
    const menuButton = new GlassButton(this, width / 2, buttonY + 140, {
      text: 'Main Menu',
      iconKey: ICONS.ARROW_BACK,
      fontSize: 20,
      width: 200,
      height: 52,
      style: 'secondary',
      onClick: () => this.returnToMenu(),
    });
    menuButton.setScrollFactor(0);
  }

  private createIconButton(
    x: number,
    y: number,
    width: number,
    height: number,
    iconKey: string,
    label: string,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Background
    const bg = this.add.rectangle(0, 0, width, height, 0x000000, 0.8);
    bg.setStrokeStyle(2, 0xffffff, 0.5);
    bg.setOrigin(0, 0); // Origin at top-left
    container.add(bg);

    // Icon (larger, centered)
    const iconSize = 32; // Larger icon
    const icon = this.add.image(width / 2, height / 2 - 10, iconKey);
    icon.setDisplaySize(iconSize, iconSize);
    container.add(icon);

    // Label (centered below icon)
    const labelText = this.add.text(width / 2, height / 2 + 15, label, {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: '14px',
      color: GAME_CONSTANTS.UI.COLOR_PRIMARY,
      resolution: Math.max(3, window.devicePixelRatio),
    });
    labelText.setOrigin(0.5);
    container.add(labelText);

    // Make interactive
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', onClick);

    container.setScrollFactor(0);
    return container;
  }

  private toggleSfx(): void {
    HapticManager.settingsToggle();
    SoundManager.toggleSfxMute();

    // Update icon
    if (this.sfxToggleButton) {
      const icon = this.sfxToggleButton.getAt(1) as Phaser.GameObjects.Image;
      const newIconKey = SoundManager.isSfxMuted() ? 'icon-sfx-off' : 'icon-sfx-on';
      icon.setTexture(newIconKey);
    }
  }

  private toggleMusic(): void {
    HapticManager.settingsToggle();
    SoundManager.toggleMusicMute();

    // Update icon
    if (this.musicToggleButton) {
      const icon = this.musicToggleButton.getAt(1) as Phaser.GameObjects.Image;
      const newIconKey = SoundManager.isMusicMuted() ? 'icon-music-off' : 'icon-music-on';
      icon.setTexture(newIconKey);
    }
  }

  private toggleHaptic(): void {
    HapticManager.toggle();

    // Update icon opacity based on state
    if (this.hapticToggleButton) {
      const icon = this.hapticToggleButton.getAt(1) as Phaser.GameObjects.Image;
      icon.setAlpha(HapticManager.isEnabled() ? 1.0 : 0.5);
    }
  }

  private resumeGame(): void {
    // Emit resume event to GameScene
    this.game.events.emit(GAME_EVENTS.GAME_RESUME);

    // Stop pause scene
    this.scene.stop('PauseScene');
  }

  private resetGame(): void {
    // Confirm with user
    const confirmed = confirm('Reset to ground level? This will lose your current progress.');
    if (!confirmed) return;

    // Set flag to prevent GameScene from auto-saving on shutdown
    this.registry.set('skipAutoSave', true);

    // Stop all scenes (shutdown will check skipAutoSave flag)
    this.scene.stop('PauseScene');
    this.scene.stop('UIScene');
    this.scene.stop('GameScene');

    // Clear saved state to start fresh from ground level
    SaveStateManager.clearState();

    // Don't clear flag here - GameScene.create() will clear it
    // This ensures flag is still true when shutdown event fires

    // Restart game fresh from ground level
    this.scene.start('GameScene');
  }

  private async shareGame(): Promise<void> {
    try {
      // Get current height and timer from UIScene
      const uiScene = this.scene.get('UIScene') as any;
      const heightText = uiScene?.heightText?.text || 'Height: 0m';
      const timerText = uiScene?.timerText?.text || '00:00:00:00';
      const highscoreText = uiScene?.highscoreText?.text || 'Best: 0m';

      // Hide pause scene temporarily to take screenshot
      this.scene.setVisible(false, 'PauseScene');

      // Wait one frame for visibility to update
      await new Promise(resolve => setTimeout(resolve, 50));

      // Take screenshot using Phaser's renderer snapshot
      this.game.renderer.snapshot((snapshot: Phaser.Display.Color | HTMLImageElement) => {
        // Show pause scene again
        this.scene.setVisible(true, 'PauseScene');

        // Check if snapshot is an HTMLImageElement
        if (!(snapshot instanceof HTMLImageElement)) {
          console.error('Snapshot is not an image');
          return;
        }

        // Convert image to blob
        const canvas = document.createElement('canvas');
        canvas.width = snapshot.width;
        canvas.height = snapshot.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          console.error('Failed to get canvas context');
          return;
        }

        ctx.drawImage(snapshot, 0, 0);

        canvas.toBlob(async blob => {
          if (!blob) {
            console.error('Failed to create screenshot blob');
            return;
          }

          const file = new File([blob], 'blobo-score.png', { type: 'image/png' });

          // Share text
          const shareText = `${ICONS.TROPHY}  Beat my Blobo score!\n${highscoreText}\n${ICONS.TIMER}  Time: ${timerText}\n${ICONS.HEIGHT}  Current: ${heightText}`;

          const shareData = {
            title: 'Beat my Blobo score!',
            text: shareText,
            files: [file],
          };

          // Check if Web Share API is available
          if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            try {
              await navigator.share(shareData);
              console.log('Score shared successfully!');
            } catch (shareError) {
              console.log('Share cancelled or failed:', shareError);
            }
          } else {
            // Fallback: copy text to clipboard
            try {
              await navigator.clipboard.writeText(shareText);
              alert('Score copied to clipboard! (Share not available on this device)');
            } catch (clipError) {
              console.error('Clipboard copy failed:', clipError);
              alert('Could not copy to clipboard. Please try again.');
            }
          }
        }, 'image/png');
      });
    } catch (error) {
      console.error('Share failed:', error);
      alert('Failed to share score');
      // Make sure pause scene is visible again in case of error
      this.scene.setVisible(true, 'PauseScene');
    }
  }

  private returnToMenu(): void {
    // Stop all game scenes (progress auto-saved by GameScene shutdown)
    this.scene.stop('PauseScene');
    this.scene.stop('UIScene');
    this.scene.stop('GameScene');

    // Return to main menu
    this.scene.start('MainMenuScene');
  }
}
